import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCatalogueByMetadataId,
  getCatalogue,
  connectCatalogue,
  disconnectCatalogue,
} from '../../../reducers/wca/product/product-action';

import {
  resetProductState,
  resetCatalogueState,
} from '../../../reducers/wca/product/product-slice';
import Loader from '../../../components/Loader';
import { useLayout } from '../../../context/LayoutContext';
import { CustomButton } from '../../../components/CustomButton';
import CustomSaveModel from '../../../components/CustomeSaveModel';
import { InputText } from 'primereact/inputtext';
import { Text } from '../../../components/Text';

const CatalogueConfig = ({ metadataId }) => {
  const dispatch = useDispatch();
  const { toast } = useLayout();

  // 🔹 Redux state
  const { catalogueByMetadata, catalogLoading, connectLoading, disconnectLoading, error, success } =
    useSelector((state) => state.productTableWCA);

  // 🔹 Derived state
  const catalogInfo = catalogueByMetadata?.data?.[0] || null;

  // 🔹 Local UI state
  const [catalogIdInput, setCatalogIdInput] = useState('');
  const [fetchedCatalog, setFetchedCatalog] = useState(null);
  const [catalogFetching, setCatalogFetching] = useState(false);
  const [catalogFetchError, setCatalogFetchError] = useState(null);

  const [showSave, setShowSave] = useState(false);
  const [actionType, setActionType] = useState(null); // 'connect' | 'disconnect'

  // =============================
  // 🔹 Load catalog on mount
  // =============================
  useEffect(() => {
    if (metadataId) {
      dispatch(getCatalogueByMetadataId(metadataId));
    }
  }, [metadataId]);

  // =============================
  // 🔹 Reset success
  // =============================
  useEffect(() => {
    if (success) {
      dispatch(resetCatalogueState());
    }
  }, [success]);

  // =============================
  // 🔹 Fetch catalog by ID
  // =============================
  const handleFetchCatalogById = async () => {
    if (!catalogIdInput.trim()) return;

    setCatalogFetching(true);
    setCatalogFetchError(null);
    setFetchedCatalog(null);

    try {
      const res = await dispatch(getCatalogue(catalogIdInput)).unwrap();
      setFetchedCatalog(res);
    } catch (err) {
      const msg = 'Could not find a catalog with that ID. Please check and try again.';

      setCatalogFetchError(msg);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: msg,
      });
    } finally {
      setCatalogFetching(false);
    }
  };

  const handleConfirm = async () => {
    try {
      if (actionType === 'connect') {
        await dispatch(
          connectCatalogue({
            metadataid: metadataId,
            data: { catalogId: fetchedCatalog.id },
          }),
        ).unwrap();

        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Catalogue connected successfully',
        });

        setFetchedCatalog(null);
        setCatalogIdInput('');
      }

      if (actionType === 'disconnect') {
        await dispatch(
          disconnectCatalogue({
            metadataid: metadataId,
            data: { catalogId: catalogInfo.id },
          }),
        ).unwrap();

        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Catalogue disconnected successfully',
        });
      }

      dispatch(getCatalogueByMetadataId(metadataId));
    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong',
      });
    } finally {
      setShowSave(false);
      setActionType(null);
    }
  };

  const getConfirmationText = () => {
    if (actionType === 'connect') {
      return `Are you sure you want to connect this catalogue?`;
    }

    if (actionType === 'disconnect') {
      return `Are you sure you want to disconnect "${catalogInfo?.name}"?`;
    }

    return '';
  };

  const dialogFooter = () => {
    return (
      <div className="flex justify-content-end gap-2">
        <CustomButton
          label="Cancel"
          type="button"
          varient="cancel"
          onClick={() => setShowSave(false)}
        />

        <CustomButton label="Confirm" type="button" varient="filled" onClick={handleConfirm} />
      </div>
    );
  };

  // =============================
  // 🔹 UI
  // =============================
  return (
    <div className="catalog-config-section">
      <CustomSaveModel
        showSave={showSave}
        header={getConfirmationText()}
        dialogFooter={dialogFooter()}
        onHide={() => setShowSave(false)}
      />

      <Loader visible={connectLoading || disconnectLoading || catalogLoading} />
      <Text type={'heading'}>Catalog Configuration</Text>

      {/* 🔹 Error */}
      {/* {error && !catalogInfo && !catalogLoading && (
        <div className="error-message">
          <p>Failed to load catalog</p>

          <CustomButton
            label="Retry"
            varient="outline"
            onClick={() => dispatch(getCatalogueByMetadataId(metadataId))}
          />
        </div>
      )} */}

      {/* 🔹 No Catalog */}
      {!catalogLoading && !catalogInfo && (
        <div className="catalog-connect-container">
          <div className="mt-2">
            <Text type={'sub-heading'}>No catalog connected to this metadata.</Text>
          </div>

          <div className="catalog-connect-box border-1 surface-border border-round-xl p-4 shadow-2 mt-3 w-full lg:w-5 ">
            <div className="mb-3">
              <h3 className="m-0 text-lg font-semibold">Connect a Catalog</h3>
              <p className="text-sm text-color-secondary mt-1">
                Enter a catalog ID to fetch and connect it to this metadata.
              </p>
            </div>

            <div className="flex gap-2 align-items-center w-full">
              <InputText
                placeholder="Enter catalog ID"
                value={catalogIdInput}
                onChange={(e) => {
                  setCatalogIdInput(e.target.value);
                  setFetchedCatalog(null);
                  setCatalogFetchError(null);
                }}
                className="flex-1"
              />

              <CustomButton
                varient="filled"
                type="button"
                label={catalogFetching ? 'Fetching...' : 'Fetch'}
                onClick={handleFetchCatalogById}
                disabled={catalogFetching || !catalogIdInput.trim()}
              />
            </div>

            {catalogFetchError && (
              <div className="mt-2 text-sm text-red-500 flex align-items-center gap-2">
                <i className="pi pi-exclamation-circle"></i>
                <span>{catalogFetchError}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🔹 Catalog Connected */}
      {fetchedCatalog && (
        <div className="catalog-preview-card shadow-2 border-round p-3 mt-3 w-full lg:w-5">
          <h4 className="mb-2">Catalog Preview</h4>

          <div className="flex flex-column gap-2">
            <div>
              <strong>Name:</strong> {fetchedCatalog.name || '—'}
            </div>

            <div>
              <strong>ID:</strong> <span className="code-badge">{fetchedCatalog.id}</span>
            </div>
          </div>

          <div className="flex gap-2 mt-3 justify-content-end">
            <CustomButton
              varient="outline"
              label="Change"
              onClick={() => {
                setFetchedCatalog(null);
                setCatalogIdInput('');
              }}
            />

            <CustomButton
              varient="filled"
              label="Connect"
              onClick={() => {
                setActionType('connect');
                setShowSave(true);
              }}
            />
          </div>
        </div>
      )}

      {catalogInfo && (
        <div className="catalog-connected-card shadow-2 border-round p-3 mt-3  w-full lg:w-5 ">
          <h4 className="mb-2">Connected Catalog</h4>

          <div className="flex flex-column gap-2">
            <div>
              <strong>Name:</strong> {catalogInfo.name || '—'}
            </div>

            <div>
              <strong>ID:</strong> <span className="code-badge">{catalogInfo.id}</span>
            </div>
          </div>

          <div className="flex gap-2 mt-3 justify-content-end">
            <CustomButton
              varient="outline"
              label="Disconnect"
              onClick={() => {
                if (!showSave) {
                  setActionType('disconnect');
                  setShowSave(true);
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogueConfig;
